'use client';

/**
 * MediaHeroCard — full-width featured media card (design 4a "reconciled
 * HERO video").
 *
 * The single-reference treatment for a RAG turn: title + category chip laid
 * OVER the 16:9 thumbnail, duration badge top-right, 58px play button. A
 * video swaps to an inline YouTube iframe on click; an article is a plain
 * link with the same chrome (favicon + domain in place of the category
 * chip, no play/duration).
 *
 * Multiple references use RelatedMediaPanel instead — see rag-answer-block.
 */

import React, { memo, useMemo, useState } from 'react';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import {
  getYouTubeId,
  youTubeThumbnailUrl,
  youTubeEmbedUrl,
} from '@/lib/youtube';
import { useYouTubeDuration } from '@/hooks/useYouTubeDuration';
import { defaultPreview } from './default-preview';

interface MediaHeroCardProps {
  refItem: RAGReference;
  className?: string;
}

/** Shared card chrome (design 4a): 16px radius, warm border, deep soft
 *  shadow. */
const HERO_CHROME =
  'relative block w-full overflow-hidden rounded-[16px] border-[1px] border-[#e6ddd0] shadow-[0_16px_40px_-24px_rgba(49,54,78,0.55)]';

/** Scrim so the overlaid title stays legible on any thumbnail. */
const HERO_SCRIM =
  'pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.05)_30%,rgba(0,0,0,0.28)_62%,rgba(0,0,0,0.66)_100%)]';

export const MediaHeroCard = memo(function MediaHeroCard({
  refItem,
  className,
}: MediaHeroCardProps) {
  const youtubeId = getYouTubeId(refItem.url);
  if (youtubeId) {
    return (
      <VideoHero
        youtubeId={youtubeId}
        refItem={refItem}
        className={className}
      />
    );
  }
  return <ArticleHero refItem={refItem} className={className} />;
});

// ── Video ──────────────────────────────────────────────────────────

function VideoHero({
  youtubeId,
  refItem,
  className,
}: {
  youtubeId: string;
  refItem: RAGReference;
  className?: string;
}): React.ReactElement {
  const [playing, setPlaying] = useState(false);
  const duration = useYouTubeDuration(refItem.url);
  // Shorts (9:16) render in a portrait box like YouTube Shorts, not 16:9 landscape.
  const isShort = /\/shorts\//.test(refItem.url || '');
  const mediaAspect = isShort
    ? 'aspect-[9/16] mx-auto w-full max-w-[300px]'
    : 'aspect-video w-full';

  if (playing) {
    return (
      <div className={cn(HERO_CHROME, mediaAspect, 'bg-black', className)}>
        <iframe
          src={youTubeEmbedUrl(youtubeId, { autoplay: true })}
          title={refItem.title || 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="size-full border-0"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={refItem.title ? `Play ${refItem.title}` : 'Play video'}
      className={cn(HERO_CHROME, 'group cursor-pointer p-0', className)}
    >
      <div className={cn('relative', mediaAspect)}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={youTubeThumbnailUrl(youtubeId)}
          alt=""
          className="absolute inset-0 size-full object-cover"
          onError={(e) => {
            const img = e.currentTarget;
            if (img.dataset.fallback) return;
            img.dataset.fallback = '1';
            img.src = defaultPreview(refItem).src;
          }}
        />
        <span className={HERO_SCRIM} />
        <PlayButton />
        {duration && <DurationBadge duration={duration} />}
        <span className="absolute inset-x-[20px] bottom-[18px] flex flex-col items-start gap-2 text-left">
          {refItem.category && (
            <span className="inline-flex items-center gap-[7px]">
              <YouTubeChip />
              <span className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-white/[0.92]">
                {refItem.category}
              </span>
            </span>
          )}
          <span className="max-w-[82%] font-[family-name:var(--font-lora)] text-[18px] font-semibold leading-[1.25] text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.4)] sm:text-[22px]">
            {refItem.title}
          </span>
        </span>
      </div>
    </button>
  );
}

// ── Article ────────────────────────────────────────────────────────

function ArticleHero({
  refItem,
  className,
}: {
  refItem: RAGReference;
  className?: string;
}): React.ReactElement {
  const preview = useMemo(() => defaultPreview(refItem), [refItem]);
  const host = useMemo(() => hostInfo(refItem.url), [refItem.url]);

  return (
    <a
      href={refItem.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(HERO_CHROME, 'group no-underline', className)}
      style={
        preview.category === 'wisdom'
          ? {
              background:
                'radial-gradient(circle at 50% 30%, #fff3df 0%, #f6d9a6 68%, #eec988 100%)',
            }
          : { background: '#000' }
      }
    >
      <div className="relative aspect-video">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={preview.src}
          alt=""
          className={cn(
            'absolute inset-0 size-full',
            preview.fit === 'cover'
              ? 'object-cover'
              : 'object-contain object-bottom'
          )}
        />
        <span className={HERO_SCRIM} />
        <span className="absolute inset-x-[20px] bottom-[18px] flex flex-col items-start gap-2 text-left">
          <span className="inline-flex items-center gap-[7px]">
            {host.favicon && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={host.favicon}
                alt=""
                className="size-[15px] flex-none rounded-[3px] bg-white/90 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-white/[0.92]">
              {host.domain}
            </span>
          </span>
          <span className="max-w-[82%] font-[family-name:var(--font-lora)] text-[18px] font-semibold leading-[1.25] text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.4)] sm:text-[22px]">
            {refItem.title}
          </span>
        </span>
      </div>
    </a>
  );
}

// ── Shared bits (also used by RelatedMediaPanel) ───────────────────

/** 58px white play disc with the brand-orange triangle (design 4a). */
export function PlayButton({
  size = 58,
}: {
  size?: number;
}): React.ReactElement {
  return (
    <span
      aria-hidden
      className="absolute left-1/2 top-[46%] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/[0.94] shadow-[0_10px_26px_rgba(0,0,0,0.35)] transition-transform group-hover:scale-105"
      style={{ width: size, height: size }}
    >
      <svg
        width={Math.round(size * 0.4)}
        height={Math.round(size * 0.4)}
        viewBox="0 0 24 24"
        fill="#E36001"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  );
}

/** "5:12" badge, top-right of a video thumbnail (design 4a). */
export function DurationBadge({
  duration,
  position = 'top-right',
}: {
  duration: string;
  position?: 'top-right' | 'bottom-right';
}): React.ReactElement {
  return (
    <span
      className={cn(
        'absolute rounded-[6px] bg-black/60 px-[9px] py-[3px] text-[12px] font-semibold text-white',
        position === 'top-right'
          ? 'right-[13px] top-[13px]'
          : 'bottom-[10px] right-[10px]'
      )}
    >
      {duration}
    </span>
  );
}

/** 19px red YouTube square with a white play triangle (design 4a chip). */
export function YouTubeChip({
  size = 19,
}: {
  size?: number;
}): React.ReactElement {
  return (
    <span
      aria-hidden
      className="flex items-center justify-center rounded-[5px] bg-[#FF0000]"
      style={{ width: size, height: size }}
    >
      <svg
        width={Math.round(size * 0.53)}
        height={Math.round(size * 0.53)}
        viewBox="0 0 24 24"
        fill="#fff"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  );
}

/** Domain + favicon for an article's source row. */
export function hostInfo(url: string): {
  domain: string;
  favicon: string | null;
} {
  try {
    const h = new URL(url).hostname;
    return {
      domain: h.replace(/^www\./, ''),
      favicon: `https://www.google.com/s2/favicons?domain=${h}&sz=32`,
    };
  } catch {
    return { domain: url, favicon: null };
  }
}

export default MediaHeroCard;
